import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CURRENT_USER_QUERY_KEY } from "../auth/types";
import { client } from "../client";
import { USER_CLUBS_QUERY_KEY } from "../clubs/types";
import { MONTHLY_LEADERBOARD_QUERY_KEY } from "../leaderboards/types";
import { TREES_PROGRESS_QUERY_KEY } from "../trees/types";
import {
  CLAIM_MILESTONE_MUTATION_KEY,
  ClaimMilestoneRequest,
  ClaimMilestoneResponse,
  MILESTONES_QUERY_KEY,
  MILESTONES_RECAP_QUERY_KEY,
  MilestonesRecapResponse,
  MilestonesResponse,
} from "./types";

/**
 * Hook for fetching the milestones recap data
 * @returns Query result with milestones recap data
 */
export function useMilestonesRecap() {
  return useQuery<MilestonesRecapResponse, Error>({
    queryKey: MILESTONES_RECAP_QUERY_KEY,
    queryFn: async () => {
      const response = await client.milestones.recap.$get();

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(
          errorData.message || "Failed to fetch milestones recap"
        );
      }

      return await response.json();
    },
  });
}

/**
 * Hook for fetching weekly milestones data directly from the API
 * @returns Query result with weekly milestones data
 */
export function useWeeklyMilestones() {
  return useQuery<MilestonesResponse, Error>({
    queryKey: MILESTONES_QUERY_KEY,
    queryFn: async () => {
      const response = await client.milestones.$get();

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(
          errorData.message || "Failed to fetch weekly milestones"
        );
      }

      return await response.json();
    },
  });
}

/**
 * Hook for claiming a milestone
 * @returns Mutation for claiming a milestone
 */
export function useClaimMilestone() {
  const queryClient = useQueryClient();

  return useMutation<ClaimMilestoneResponse, Error, ClaimMilestoneRequest>({
    mutationKey: CLAIM_MILESTONE_MUTATION_KEY,
    mutationFn: async (data) => {
      const response = await client.milestones.claim.$post({
        json: data,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to claim milestone");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: MILESTONES_RECAP_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: USER_CLUBS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TREES_PROGRESS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: MONTHLY_LEADERBOARD_QUERY_KEY,
      });
    },
  });
}
